// src/pages/register/register.tsx
//
// Tenant onboarding. This was a template stub: uncontrolled inputs with no
// state, no tenant-type selector, and a submit control that was a <Link to="/">
// — so it navigated to the dashboard and created nothing. Anyone who filled it
// in was told nothing and got nothing.
//
// It now posts the real POST /tenants/onboard payload, which creates the tenant,
// its business entity, first store, warehouse and admin user in one transaction.
//
// The TYPE SELECTOR is the important part. The backend used to collect a tenant
// type at signup and drop it, so every tenant became RETAIL_PHARMACY — which
// broke clinical gating and prescription routing for hospitals and clinics, and
// was unrecoverable afterwards because the chosen value was never stored. The
// field is persisted now, so the form must actually ask.

import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Form, Input, Select, Button, Alert, Typography } from "antd";
import { all_routes } from "../../Router/all_routes";
import BASE_URL from "../../core/redux/apis/api";
import { getApiErrorMessageWithRef } from "../../core/utils/apiError";

const { Title, Text } = Typography;

/** Mirrors the backend TenantType enum. */
const TENANT_TYPES = [
  { value: "RETAIL_PHARMACY", label: "Retail pharmacy", store: "RETAIL_STORE" },
  { value: "WHOLESALE_PHARMACY", label: "Wholesale pharmacy", store: "WHOLESALE_WAREHOUSE" },
  { value: "DISTRIBUTOR", label: "Distributor", store: "WHOLESALE_WAREHOUSE" },
  { value: "HOSPITAL", label: "Hospital", store: "HOSPITAL_OPD" },
  { value: "CLINIC", label: "Clinic", store: "CLINIC_BRANCH" },
  { value: "DIAGNOSTIC_LAB", label: "Diagnostic lab", store: "DIAGNOSTIC_CENTER" },
];

/** Lower-case, hyphenated, no leading/trailing hyphen — matches the slug rule. */
const slugify = (v: string) =>
  v.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

const Register: React.FC = () => {
  const route = all_routes;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const tenantName = Form.useWatch("tenantName", form);
  const derivedSlug = useMemo(() => slugify(tenantName ?? ""), [tenantName]);

  const onFinish = async (v: any) => {
    setSubmitting(true);
    setError(null);
    try {
      // The store type follows the tenant type — a hospital's first location is
      // an OPD, not a retail counter. One less thing to get wrong, and the
      // backend enum will not accept a mismatch anyway.
      const storeType =
        TENANT_TYPES.find((t) => t.value === v.type)?.store ?? "RETAIL_STORE";

      const res = await fetch(`${BASE_URL}/tenants/onboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant: {
            name: v.tenantName,
            slug: v.slug || slugify(v.tenantName),
            legalName: v.legalName || undefined,
            contactEmail: v.email,
            type: v.type,
          },
          businessEntity: { name: v.legalName || v.tenantName },
          store: {
            name: `${v.tenantName} — Main`,
            code: `${slugify(v.tenantName).slice(0, 8).toUpperCase()}-MAIN`,
            isMain: true,
            type: storeType,
          },
          warehouse: {
            name: `${v.tenantName} — Main store`,
            code: `${slugify(v.tenantName).slice(0, 8).toUpperCase()}-WH`,
          },
          adminUser: {
            name: v.adminName,
            email: v.email,
            password: v.password,
            phone: v.phone || undefined,
          },
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        // A real Error, carrying the shape getApiErrorMessageWithRef reads —
        // so the correlation id survives to the message.
        const err: any = new Error(body?.message || "Request failed");
        err.status = res.status;
        err.data = body;
        throw err;
      }
      setDone(true);
      setTimeout(() => navigate(route.signin), 1500);
    } catch (e) {
      setError(getApiErrorMessageWithRef(e, "Could not create the account."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="account-content">
      <div className="login-wrapper register-wrap bg-img">
        <div className="login-content authent-content">
          <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
            <div className="login-userset">
              <div className="login-userheading">
                <Title level={3} className="mb-1">Create an account</Title>
                <Text type="secondary">
                  Sets up the business, its first location and your admin sign-in.
                </Text>
              </div>

              {done && (
                <Alert
                  className="mb-3"
                  type="success"
                  showIcon
                  message="Account created"
                  description="Taking you to sign in…"
                />
              )}
              {error && <Alert className="mb-3" type="error" showIcon message={error} />}

              <Form.Item
                name="tenantName"
                label="Business name"
                rules={[{ required: true, min: 2, message: "Enter the business name." }]}
              >
                <Input placeholder="e.g. Medi Retail Group" autoComplete="organization" />
              </Form.Item>

              <Form.Item
                name="type"
                label="Business type"
                extra="Sets clinical features and prescription routing. It cannot be guessed later."
                rules={[{ required: true, message: "Choose the business type." }]}
              >
                <Select placeholder="Select a type" options={TENANT_TYPES.map(({ value, label }) => ({ value, label }))} />
              </Form.Item>

              <Form.Item
                name="slug"
                label="Workspace address"
                extra={derivedSlug ? `Leave blank to use “${derivedSlug}”.` : "Derived from the business name if left blank."}
                rules={[
                  {
                    pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                    message: "Lower-case letters, numbers and hyphens only.",
                  },
                ]}
              >
                <Input placeholder={derivedSlug || "medi-retail-group"} />
              </Form.Item>

              <Form.Item name="legalName" label="Registered legal name (optional)">
                <Input placeholder="As registered with CAC" />
              </Form.Item>

              <Form.Item
                name="adminName"
                label="Your name"
                rules={[{ required: true, min: 2, message: "Enter your name." }]}
              >
                <Input autoComplete="name" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Enter your email." },
                  { type: "email", message: "That does not look like an email address." },
                ]}
              >
                <Input autoComplete="email" />
              </Form.Item>

              <Form.Item name="phone" label="Phone (optional)">
                <Input autoComplete="tel" />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                extra="At least 8 characters, with an upper-case letter, a lower-case letter and a number."
                rules={[
                  { required: true, message: "Choose a password." },
                  { min: 8, message: "At least 8 characters." },
                  { pattern: /[a-z]/, message: "Include a lower-case letter." },
                  { pattern: /[A-Z]/, message: "Include an upper-case letter." },
                  { pattern: /[0-9]/, message: "Include a number." },
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm password"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Confirm the password." },
                  ({ getFieldValue }) => ({
                    validator: (_, value) =>
                      !value || getFieldValue("password") === value
                        ? Promise.resolve()
                        : Promise.reject(new Error("The passwords do not match.")),
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>

              <Form.Item className="mb-2">
                {/* Was <Link to="/">, which navigated away and created nothing. */}
                <Button type="primary" htmlType="submit" block loading={submitting} disabled={done}>
                  Create account
                </Button>
              </Form.Item>

              <div className="signinform text-center">
                <Text type="secondary">Already have an account? </Text>
                <Link to={route.signin} className="hover-a">Sign in</Link>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Register;
